/**
 * Shared Hook for Async Select Components
 * Extracts common state management, pagination, debouncing, and API logic
 */

import {
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  SelectOption,
  fetchAsyncSelectOptions,
  fetchAsyncSelectValueById,
  fetchAsyncSelectValuesByIds,
} from '../utils/asyncSelectApi';

const DEBOUNCE_DELAY = 300;
const DEFAULT_PAGE_SIZE = 20;

export interface UseAsyncSelectOptions {
  apiEndpoint: string;
  searchField?: string;
  valueField: string;
  labelField: string;
  pageSize?: number;
}

export interface UseAsyncSelectReturn {
  /**
   * All loaded options (for controlled mode)
   */
  options: SelectOption[];

  /**
   * Load options with debouncing for react-select
   * Returns initial page of results
   * @param inputValue - Search term
   * @param callback - Callback to return options to react-select
   */
  loadOptions: (
    inputValue: string,
    callback: (options: SelectOption[]) => void
  ) => void;

  /**
   * Handle infinite scroll at menu bottom
   * Loads next page and updates options state
   */
  handleMenuScrollToBottom: () => Promise<void>;

  /**
   * Callback ref for scroll container to enable scroll position tracking
   */
  scrollContainerRef: (element: HTMLDivElement | null) => void;

  /**
   * Fetch single selected value by ID
   * Used for displaying initial value in single-select
   * @param id - Value ID to fetch
   * @returns Promise with SelectOption or null
   */
  fetchSelectedValue: (id: string) => Promise<SelectOption | null>;

  /**
   * Fetch multiple selected values by IDs
   * Used for displaying initial values in multi-select
   * @param ids - Array of value IDs to fetch
   * @returns Promise with array of SelectOption
   */
  fetchSelectedValues: (ids: string[]) => Promise<SelectOption[]>;

  /**
   * Reset pagination state (useful when search term changes externally)
   */
  resetPagination: () => void;

  /**
   * Loading state for infinite scroll
   */
  isLoadingMore: boolean;
}

/**
 * Custom hook for async select components
 * Provides shared state management, pagination, debouncing, and API interactions
 * Now manages options state internally for infinite scroll support
 *
 * @example
 * const { loadOptions, handleMenuScrollToBottom, options, isLoadingMore } = useAsyncSelect({
 *   apiEndpoint: '/grades',
 *   searchField: 'grade_name',
 *   valueField: 'grade_id',
 *   labelField: 'grade_name',
 * });
 */
export function useAsyncSelect(
  options: UseAsyncSelectOptions
): UseAsyncSelectReturn {
  const {
    apiEndpoint,
    searchField,
    valueField,
    labelField,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;

  // Pagination state
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const lastSearchRef = useRef('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for all loaded options (controlled mode)
  const [allOptions, setAllOptions] = useState<SelectOption[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Scroll position tracking
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const savedScrollTopRef = useRef<number>(0);
  const savedScrollHeightRef = useRef<number>(0);
  const shouldRestoreScrollRef = useRef<boolean>(false);
  const lastLoadTimeRef = useRef<number>(0);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Restore scroll position when loading state changes
   * Use useLayoutEffect to run synchronously before browser paint
   * Restores both when loading starts (footer added) and ends (footer removed)
   * For infinite scroll (appending items), we keep the exact same scroll position
   * This prevents visible scroll jumping
   */
  useLayoutEffect(() => {
    if (shouldRestoreScrollRef.current && scrollElementRef.current) {
      const element = scrollElementRef.current;
      const targetScrollTop = savedScrollTopRef.current;

      // Restore to exact same position (items are appended at bottom)
      element.scrollTop = targetScrollTop;
      shouldRestoreScrollRef.current = false;
    }
  }, [isLoadingMore]);

  /**
   * Callback ref to track scroll container element
   */
  const scrollContainerRef = useCallback((element: HTMLDivElement | null) => {
    scrollElementRef.current = element;
  }, []);

  /**
   * Fetch options from API with pagination
   * Internal helper function
   */
  const fetchOptions = useCallback(
    async (
      searchTerm: string,
      offset: number
    ): Promise<{ options: SelectOption[]; hasMore: boolean }> => {
      try {
        return await fetchAsyncSelectOptions(
          {
            apiEndpoint,
            searchField,
            valueField,
            labelField,
          },
          searchTerm,
          pageSize,
          offset
        );
      } catch (error) {
        console.error('Error fetching async select options:', error);
        return { options: [], hasMore: false };
      }
    },
    [apiEndpoint, searchField, valueField, labelField, pageSize]
  );

  /**
   * Load options with debouncing for react-select
   * Replaces options on new search
   */
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: SelectOption[]) => void): void => {
      // Clear existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Reset pagination when search term changes
      if (inputValue !== lastSearchRef.current) {
        offsetRef.current = 0;
        hasMoreRef.current = true;
        lastSearchRef.current = inputValue;
      }

      // Debounce the API call
      debounceTimeoutRef.current = setTimeout(async () => {
        const { options, hasMore } = await fetchOptions(inputValue, 0);

        offsetRef.current = pageSize;
        hasMoreRef.current = hasMore;
        setAllOptions(options);
        callback(options);
      }, DEBOUNCE_DELAY);
    },
    [fetchOptions, pageSize]
  );

  /**
   * Handle infinite scroll at menu bottom
   * Loads next page and appends to options state
   */
  const handleMenuScrollToBottom = useCallback(async (): Promise<void> => {
    if (!hasMoreRef.current) {
      return;
    }

    if (isLoadingMore) {
      return;
    }

    // Add cooldown to prevent immediate re-triggering after scroll restoration
    const timeSinceLastLoad = Date.now() - lastLoadTimeRef.current;
    if (timeSinceLastLoad < 500) {
      return;
    }

    // Save current scroll position before triggering load
    if (scrollElementRef.current) {
      savedScrollTopRef.current = scrollElementRef.current.scrollTop;
      savedScrollHeightRef.current = scrollElementRef.current.scrollHeight;
      shouldRestoreScrollRef.current = true;
    }

    lastLoadTimeRef.current = Date.now();
    setIsLoadingMore(true);

    try {
      const { options: newOptions, hasMore } = await fetchOptions(
        lastSearchRef.current,
        offsetRef.current
      );

      if (newOptions.length > 0) {
        setAllOptions(prevOptions => [...prevOptions, ...newOptions]);
        offsetRef.current += pageSize;
      } else {
        shouldRestoreScrollRef.current = false; // Don't restore if no new data
      }

      hasMoreRef.current = hasMore;
    } catch (error) {
      console.error('[useAsyncSelect] ❌ Error loading more options:', error);
      shouldRestoreScrollRef.current = false; // Don't restore on error
    } finally {
      // Save scroll position again before removing loading footer
      // The footer disappearing will cause react-select to reset scroll
      if (scrollElementRef.current && shouldRestoreScrollRef.current) {
        savedScrollTopRef.current = scrollElementRef.current.scrollTop;
        savedScrollHeightRef.current = scrollElementRef.current.scrollHeight;
      }
      setIsLoadingMore(false);
    }
  }, [fetchOptions, pageSize, isLoadingMore]);

  /**
   * Check if API supports individual item endpoints
   * Some APIs only support list/query endpoints
   */
  const supportsItemEndpoint = useCallback((endpoint: string): boolean => {
    // These APIs don't have individual item endpoints (GET /api/{resource}/{id})
    const unsupportedEndpoints = [
      '/grades',
      '/therapeutic_areas',
      '/manufacturers',
      '/facilities',
    ];
    return !unsupportedEndpoints.includes(endpoint);
  }, []);

  /**
   * Fetch single selected value by ID
   * Tries ID endpoint first, falls back to query
   */
  const fetchSelectedValue = useCallback(
    async (id: string): Promise<SelectOption | null> => {
      try {
        // Skip individual endpoint if not supported
        if (supportsItemEndpoint(apiEndpoint)) {
          // Try to fetch by ID endpoint first (e.g., /api/manufacturers/123)
          const byIdEndpoint = `${apiEndpoint}/${id}`;
          const response = await fetch(`/api${byIdEndpoint}`);

          if (response.ok) {
            const item = await response.json();
            return {
              value: String(item[valueField]),
              label: item[labelField],
              data: item,
            };
          }
        }

        // Fallback: use shared utility with query-based fetch
        // Either endpoint doesn't exist or doesn't support individual items
        const option = await fetchAsyncSelectValueById(
          {
            apiEndpoint,
            searchField,
            valueField,
            labelField,
          },
          id
        );

        return option;
      } catch (error) {
        // Only log unexpected errors (not 404s)
        if (error instanceof TypeError) {
          console.error('Network error fetching selected value:', error);
        }
        return null;
      }
    },
    [apiEndpoint, searchField, valueField, labelField, supportsItemEndpoint]
  );

  /**
   * Fetch multiple selected values by IDs
   * Tries ID endpoints first, falls back to batch query for missing values
   */
  const fetchSelectedValues = useCallback(
    async (ids: string[]): Promise<SelectOption[]> => {
      try {
        let fetchedOptions: SelectOption[] = [];

        // Only try individual endpoints if supported
        if (supportsItemEndpoint(apiEndpoint)) {
          // Try to fetch each value by ID endpoint first
          const promises = ids.map(async id => {
            try {
              const byIdEndpoint = `${apiEndpoint}/${id}`;
              const response = await fetch(`/api${byIdEndpoint}`);

              if (response.ok) {
                const item = await response.json();
                return {
                  value: String(item[valueField]),
                  label: item[labelField],
                  data: item,
                } as SelectOption;
              }
              // 404 is expected for APIs without individual item endpoints
              // Fallback to batch query below
              return null;
            } catch (error) {
              // Network errors (not 404) are unexpected
              if (error instanceof TypeError) {
                console.error(`Network error fetching ${id}:`, error);
              }
              return null;
            }
          });

          const results = await Promise.all(promises);
          fetchedOptions = results.filter(
            (opt): opt is SelectOption => opt !== null
          );
        }

        // If any values weren't fetched via ID endpoint, try batch query
        if (fetchedOptions.length < ids.length) {
          const missingIds = ids.filter(
            id => !fetchedOptions.find(opt => opt.value === id)
          );

          const additionalOptions = await fetchAsyncSelectValuesByIds(
            {
              apiEndpoint,
              searchField,
              valueField,
              labelField,
            },
            missingIds
          );

          return [...fetchedOptions, ...additionalOptions];
        }

        return fetchedOptions;
      } catch (error) {
        console.error('Error fetching selected values:', error);
        return [];
      }
    },
    [apiEndpoint, searchField, valueField, labelField, supportsItemEndpoint]
  );

  /**
   * Reset pagination state
   */
  const resetPagination = useCallback(() => {
    offsetRef.current = 0;
    hasMoreRef.current = true;
    lastSearchRef.current = '';
    setAllOptions([]);
  }, []);

  return {
    options: allOptions,
    loadOptions,
    handleMenuScrollToBottom,
    scrollContainerRef,
    fetchSelectedValue,
    fetchSelectedValues,
    resetPagination,
    isLoadingMore,
  };
}
